import { useRef } from "react";
import { toast } from "sonner";
import { Copy, Printer } from "@phosphor-icons/react";
import { rubricToPlainText } from "@/lib/rubricTemplates";

const LEVEL_COLORS = {
  "Excellent": "#E8F0E6",
  "Good": "#F0F4ED",
  "Satisfactory": "#FFF4E8",
  "Needs Improvement": "#FCEEEA",
};

export default function PrintableRubric({ rubric }) {
  const printRef = useRef(null);

  const handleCopy = async () => {
    try {
      const text = rubricToPlainText(rubric);
      await navigator.clipboard.writeText(text);
      toast.success("Rubric copied to clipboard.");
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4 no-print">
        <button
          type="button"
          onClick={handleCopy}
          data-testid="copy-rubric-btn"
          className="inline-flex items-center gap-2 bg-white border border-[#CBD5E1] text-[#0A0A0A] rounded-xl px-4 py-2 font-semibold text-sm hover:bg-[#F8FAFC] transition-colors shadow-sm"
        >
          <Copy size={18} weight="bold" /> Copy Rubric
        </button>
        <button
          type="button"
          onClick={handlePrint}
          data-testid="print-rubric-btn"
          className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white rounded-xl px-4 py-2 font-semibold text-sm hover:bg-[#1E1E1E] transition-colors shadow-sm"
        >
          <Printer size={18} weight="bold" /> Print Rubric
        </button>
      </div>

      <div
        ref={printRef}
        data-testid="rubric-printable"
        className="print-area bg-white border border-[#0A0A0A] rounded-xl p-6 md:p-8"
      >
        <div className="mb-5 pb-4 border-b border-[#0A0A0A]">
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight leading-tight mb-3">
            {rubric.title}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5 text-sm">
            {rubric.state ? <Meta label="State" value={rubric.state} /> : null}
            <Meta label="Subject" value={rubric.subject} />
            <Meta label="Grade" value={rubric.grade} />
            <Meta label="Total Points" value={`${rubric.totalPoints}`} />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table
            data-testid="rubric-table"
            className="w-full border-collapse text-sm min-w-[640px]"
            style={{ tableLayout: "fixed" }}
          >
            <thead>
              <tr>
                <th className="border border-[#0A0A0A] bg-[#0A0A0A] text-white p-3 text-left font-bold w-1/5">
                  Criterion
                </th>
                {rubric.levels.map((lvl) => (
                  <th
                    key={lvl}
                    className="border border-[#0A0A0A] p-3 text-left font-bold"
                    style={{ backgroundColor: LEVEL_COLORS[lvl] || "#F1F5F9", width: "20%" }}
                  >
                    <div className="font-display font-black text-base">{lvl}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rubric.rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  <td className="border border-[#0A0A0A] p-3 align-top bg-[#FAFAF9] font-bold">
                    {row.criterion}
                  </td>
                  {row.levels.map((lvl) => (
                    <td
                      key={lvl.name}
                      className="border border-[#0A0A0A] p-3 align-top text-[#1F2937] leading-snug"
                    >
                      <div className="font-mono text-xs font-bold text-[#64748B] mb-1">
                        {lvl.points} pts
                      </div>
                      <div>{lvl.descriptor}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 pt-4 border-t border-[#0A0A0A] grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div>
            <span className="font-bold">Student Name:</span>{" "}
            <span className="border-b border-[#0A0A0A] inline-block min-w-[160px]">&nbsp;</span>
          </div>
          <div>
            <span className="font-bold">Date:</span>{" "}
            <span className="border-b border-[#0A0A0A] inline-block min-w-[100px]">&nbsp;</span>
          </div>
          <div className="sm:text-right">
            <span className="font-bold">Score:</span>{" "}
            <span className="border-b border-[#0A0A0A] inline-block min-w-[40px]">&nbsp;</span>{" "}
            / {rubric.totalPoints}
          </div>
        </div>

        <p className="mt-4 text-xs text-[#64748B] font-medium">
          Generated by TeacherToolsLab.com — free for classroom use.
        </p>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">
        {label}
      </div>
      <div className="font-semibold text-[#0A0A0A]">{value}</div>
    </div>
  );
}
